<template>
  <v-container class="my-0 ml-0 mr-n2 pa-0" fluid>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <p>
          Add local files to be used for embedding / RAG operations. Please note that you must use the same
          embedding model, and that API must be defined and accessible, when using the embeddings you create here.
        </p>
        <p>Steps:</p>
        <ol class="ml-6">
          <li class="pl-2">Add embedding model (if none exist)</li>
          <li class="pl-2">Set a name for your embedding collection</li>
          <li class="pl-2">Select embedding model next to collection name</li>
          <li class="pl-2">Choose files to create embeddings for</li>
          <li class="pl-2">Click "Embed" (do not refresh the page until completed)</li>
        </ol>
      </v-col>
    </v-row>
    <v-row density="compact" class="mx-0 mt-2 mb-0 pa-0">
      <v-col cols="12" density="compact" class="ma-0 pa-2 d-flex align-center">
        <Button buttonClass="ml-1" icon="mdi-plus" v-if="savedEmbeddingModels.length > 0" :click="openCreateModelOverlay" size="x-large" tooltip="New Embedding Model" variant="outlined" density="default" color="secondary"></Button>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-for="model in savedEmbeddingModels" :key="model.config.id" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <EmbeddingModelSummaryCard
          v-if="isDesktop"
          :model="model"
          @edit="openEditModelOverlay"
        />
        <EmbeddingModelCard
          v-else
          :modelId="model.config.id"
          @model-deleted="handleModelDeleted"
          @remove-card="removeModelCard"
          @saved="handleModelSaved"
        />
      </v-col>
      <v-col v-if="savedEmbeddingModels.length === 0" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <v-card @click="openCreateModelOverlay" class="ma-0 pa-2 card add-card" color="surface" density="compact" variant="elevated"
          elevation="4">
          <p>New Embedding Model</p>
          <v-icon class="add-icon" icon="mdi-plus"></v-icon>
        </v-card>
      </v-col>
    </v-row>
    <ConfigCardOverlay
      v-model="showCreateModelOverlay"
      title="New Embedding Model"
      @close="closeCreateModelOverlay"
    >
      <EmbeddingModelCard
        isOverlay
        @remove-card="closeCreateModelOverlay"
        @model-deleted="handleModelDeleted"
        @saved="handleModelSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditModelOverlay"
      title="Edit Embedding Model"
      @close="closeEditModelOverlay"
    >
      <EmbeddingModelCard
        v-if="editingModelId"
        :modelId="editingModelId"
        isOverlay
        startExpanded
        @remove-card="closeEditModelOverlay"
        @model-deleted="handleEditedModelDeleted"
        @saved="handleModelSaved"
      />
    </ConfigCardOverlay>
    <v-row density="compact" class="mx-0 mt-4 mb-0 pa-0">
      <v-col cols="4" class="ma-0 pa-2">
        <v-combobox v-model="collection" :items="collectionNames" return-object item-title="name" density="compact"
          label="New or Existing Collection Name" hide-details @update:modelValue="targetCollectionUpdated"
          @update:search="handleCollectionInput" variant="outlined" />
      </v-col>
      <v-col cols="4" class="ma-0 pa-2">
        <v-select v-model="selectedTarget" :items="savedEmbeddingModels" item-title="config.name" item-value="config" :return-object="true"
          label="Embedding Model" :rules="[() => !!selectedTarget || 'Embedding Model is required.']" density="compact"
          variant="outlined" hide-details />
      </v-col>
      <v-col cols="2" class="ma-0 pa-2">
        <v-number-input :reverse="false" controlVariant="stacked" label="Chunk Size" :hideInput="false" :inset="false"
          :step="8" :min="32" :max="2048" v-model="chunkSize" density="compact" variant="outlined"/>
      </v-col>
      <v-col cols="2" class="ma-0 pa-2">
        <v-number-input :reverse="false" controlVariant="stacked" label="Chunk Overlap" :hideInput="false"
          :inset="false" :step="4" :min="0" :max="128" v-model="chunkOverlap" density="compact" variant="outlined"/>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0" justify="end">
      <v-col cols="auto" class="ma-0 pa-2">
        <v-switch
          v-model="directoryMode"
          density="compact"
          inset
          :color="directoryMode ? 'primary' : ''"
          hide-details
          class="mt-2"
        >
          <template v-slot:prepend>
            <span>Upload entire directory (preserves folder structure)</span>
          </template>
        </v-switch>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="11" class="ma-0 pa-2">
        <v-file-input 
          v-model="fileInputModel" 
          :label="directoryMode ? 'Select directory to embed...' : 'Select files to embed...'"
          multiple 
          counter 
          clearable
          density="compact" 
          :loading="readingFiles" 
          ref="fileSelect" 
          hide-details 
          variant="outlined"
          :webkitdirectory="directoryMode"
          :directory="directoryMode"
          class="file-input-constrained-height mb-2 pt-1"
        />
        <span class="ml-11 text-caption">
          Supports text based files like markdown and code (.md, .txt, .js, .py, .ts, .html, etc.), Office formats (.docx, .xlsx, .pptx) and PDFs.<br/>
        </span>
        <span class="ml-11 text-caption">Results can vary based on document quality.</span>
      </v-col>
      <v-col cols="1" class="ma-0 pa-2 mt-1">
        <v-btn variant="outlined" @click="embedFiles" :disabled="embedDisabled" :loading="embeddingActive">Embed</v-btn>
      </v-col>
    </v-row>
    <!-- EMBEDDING PROGRESS -->
    <v-row v-if="embeddingActive" density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <v-card variant="outlined" class="pa-3">
          <v-card-title class="text-h6 pa-0 mb-2">
            Embedding Progress
          </v-card-title>
          <v-card-subtitle class="pa-0 mb-1">
            Processing {{ successfulEmbeds }} of {{ totalFilesToEmbed }} files...
          </v-card-subtitle>
          <!-- Add this new element for detailed status -->
          <v-card-text class="pa-0 mb-3 text-caption">
            Status: {{ processingStatusText }}
          </v-card-text>
          <v-row align="center" class="ma-0 pa-0">
            <v-col class="ma-0 pa-0">
              <v-progress-linear
                :model-value="embedProgress"
                color="primary"
                height="25"
                striped
              >
                <template v-slot:default="{ value }">
                  <strong>{{ Math.ceil(value) }}%</strong>
                </template>
              </v-progress-linear>
            </v-col>
            <v-col cols="auto" class="ma-0 pa-0 pl-2">
              <v-btn variant="text" color="warning" @click="cancelEmbedding" icon="mdi-cancel"></v-btn>
            </v-col>
          </v-row>
          <v-alert
            v-if="failedFiles.length > 0"
            type="warning"
            density="compact"
            class="mt-2"
          >
            Failed to embed {{ failedFiles.length }} file(s): {{ failedFiles.join(', ') }}
          </v-alert>
        </v-card>
      </v-col>
    </v-row>
    <!-- SEARCH -->
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <v-card variant="outlined" class="pa-2">
          <v-expand-transition>
            <v-container class="ma-0 pa-0 embedding-search-results" v-show="show" fluid>
              <v-row density="compact" class="ma-0 pa-0">
                <v-col cols="9" class="ma-0 pa-2 flex flex-column">
                  <v-textarea v-model="searchText" rows="9" class="flex-grow-1" no-resize auto-grow label="Search" density="compact"
                    variant="outlined" />
                </v-col>
                <v-col cols="3" class="ma-0 pa-2">
                  <v-select v-model="searchTarget" :items="savedEmbeddingModels" item-title="config.name" :return-object="true"
                    label="Embedding Model" :rules="[() => !!searchTarget || 'Embedding Model is required.']"
                    density="compact" variant="outlined" class="display-block mb-2" />
                  <v-select v-model="searchCollection" :items="localCollections" item-title="name" :return-object="true"
                    label="Local Collection" :rules="[() => !!searchCollection || 'Collection is required.']"
                    density="compact" variant="outlined" class="display-block mb-2" @update:modelValue="searchCollectionUpdated" />
                  <v-number-input v-model="searchTopK" label="Results (k)" :min="1" :max="100" :step="1" density="compact" variant="outlined" class="display-block mb-2" />
                  <Button color="primary" :click="search" icon="mdi-send-outline" size="x-large" tooltip="Search" />
                </v-col>
              </v-row>
              <v-row v-for="(r, index) in searchResults" :key="r.id || index" class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-2">
                  <v-card class="mb-0" density="compact" variant="outlined">
                    <FormattingCard :title="`${index+1}. ${decodeBase64(r.fileName ?? '')}`" :content="r.text" :lineStart="r.lines?.start" :lineEnd="r.lines?.end" />
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-expand-transition>
          <v-card-actions density="compact">
            <v-card-title v-show="!show">Collection Search</v-card-title>
            <v-spacer />
            <v-btn color="primary" size="x-large" class="mx-auto" :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              @click="toggleSearch(!show)" density="compact"></v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <!-- EMBEDDING COLLECTIONS (OpenAI Vector Stores) -->
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <v-card>
          <v-card-title class="d-flex align-center pa-3">
            <span class="text-h6">Embedding Collections</span>
            <span v-if="list.length > 0" class="text-caption ml-2">({{ totalStorage }} total)</span>
            <v-spacer />
            <span v-if="isLoadingCollections && !manualRefresh" class="text-caption text-medium-emphasis mr-2">
              Loading...
            </span>
            <v-btn
              icon
              variant="text"
              size="small"
              @click="refreshCollections"
              :loading="isLoadingCollections"
              :disabled="isLoadingCollections"
              class="ml-2 mr-4"
            >
              <v-icon>mdi-refresh</v-icon>
              <v-tooltip activator="parent" location="bottom">Refresh Collections</v-tooltip>
            </v-btn>
          </v-card-title>
          <v-divider />
          <!-- the error with update:opened on the line below is a false positive. ignore it. -->
          <v-treeview :items="listTree" :key="collectionsKey" :opened="openedItems" @update:opened="handleTreeOpened" item-value="id">
            <template v-slot:title="{ item }">
              <span
                v-if="item.itemType === 'file'"
                @click="handleFileClick(item)"
                class="file-link"
              >{{ item.title }}</span>
              <span v-if="item.itemType === 'collection'" class="text-h6">
                {{ item.title }}
              </span><br />
              <span v-if="item.itemType === 'collection'" class="text-overline">
                {{ item.subtitle }}
              </span>
            </template>
            <template v-slot:append="{ item }">
              <Button v-if="item.itemType === 'collection'" color="warning" class="mr-4"
                icon="mdi-trash-can-outline" size="large" tooltip="Delete collection" density="compact"
                :click="(e: Event) => { deleteCollection(e, item) }" />
            </template>
          </v-treeview>
        </v-card>
      </v-col>
    </v-row>
    <!-- FILE VIEWING DIALOG -->
    <v-dialog v-model="showFileDialog" max-width="900px" scrollable>
      <v-card class="pa-4">
        <v-card-title class="d-flex align-center">
          <span class="text-h6">{{ fileDialogTitle }}</span>
          <v-spacer />
          <Button v-if="selectedFile != null" icon="mdi-download-outline" size="large" width="32" tooltip="Download File" :click="() => downloadFile(selectedFile!.id, selectedFile!.filename)" />
          <Button icon="mdi-close" size="large" width="32" tooltip="Close" :click="closeFileDialog" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <div v-if="loadingFileContent" class="text-center pa-8">
            <v-progress-circular indeterminate color="primary" />
            <p class="mt-4">Loading file content...</p>
          </div>
          <FormattingCard
            v-else
            :content="fileContent"
            :expanded="'true'"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import { newId, sanitizeString, decodeBase64, modelNameToId } from '@/utils/general'
import Button from '@/components/sub/Button.vue'
import EmbeddingModelCard from '@/components/cards/EmbeddingModelCard.vue'
import EmbeddingModelSummaryCard from '@/components/cards/EmbeddingModelSummaryCard.vue'
import ConfigCardOverlay from '@/components/overlays/ConfigCardOverlay.vue'
import FormattingCard from '@/components/cards/FormattingCard.vue'
import { RosettaModel } from '@/types/msq'
import { EmbeddingCollection, EmbeddingSearchResult, FileUpload, StaticChunkStrategy, SystemEmbeddingModelConfig, UserEmbeddingModelConfig, UserVectorFile, VectorStore, VectorStoreWithFiles } from '@/types/embeddings'
import { SystemModelConfig, UserModelConfig } from '@/types/models'
import { addEventListener, removeEventListener } from '@/utils/sse'

interface EmbedFile {
  name: string
  type: string
  size: number
  lastModified: number
  content: string
}

interface TreeItem {
  id: string | number
  vectorStoreId?: string
  fileId?: string
  title: string
  itemType: 'collection' | 'file'
  safeName?: string
  subtitle?: string
  children?: TreeItem[]
}

@Options({
  props: {
    showTips: Boolean
  },
  components: {
    Button,
    EmbeddingModelCard,
    EmbeddingModelSummaryCard,
    ConfigCardOverlay,
    FormattingCard,
  },
  computed: {
    ...mapGetters([
      'username',
      'userFiles',
      'vectorStores',
      'providerEmbeddingModels',
      'modelConfigs',
      'embeddingModels',
      'embeddingModelsById',
      'localCollections',
      'searchResults',
      'isLoadingVectorStores',
      'isLoadingLocalCollections',
      'isLoadingCollections',
      'lastCollectionsRefresh'
    ])
  },
  watch: {
    embeddingModelsById: {
      handler() {
        this.updateEmbeddingModelIds()
      },
      deep: true
    },
    fileInputModel: {
      handler(newFiles: File[]) {
        if (!newFiles || newFiles.length === 0) {
          return;
        }
        const validFiles = newFiles.filter(file => isTextFileType(file.name) || isDocFileType(file.name));
        if (validFiles.length < newFiles.length) {
          const rejectedCount = newFiles.length - validFiles.length;
          this.$store.dispatch('updateSnackbar', {
            text: `${rejectedCount} file(s) were excluded because their type is not supported.`,
            timeout: 5000
          });
          // Important: Update the model value in the next tick to avoid infinite loops
          this.$nextTick(() => {
            this.fileInputModel = validFiles;
          });
        }
      },
      deep: true
    },
    list: {
      handler() {
        // When vector stores are refreshed (e.g., by listVectorStores replacing all data),
        // the detailed files fetched via fetchVectorStoreFiles are lost.
        // Clear the loadedCollections tracking so that opened collections re-fetch their files.
        this.loadedCollections.clear()
        this.$nextTick(() => {
          this.reloadOpenedCollectionFiles()
        })
      },
      deep: false
    }
  }
})
export default class Embeddings extends mixins(AnalyticsMixin) {
  public openedItems: string[] = []
  public collection: { name: string, id: string } | null = null
  public username!: string
  public showTips!: boolean
  public vectorStores!: Record<string, VectorStoreWithFiles> 
  public readingFiles: boolean = false
  public embeddingActive: boolean = false
  public files: EmbedFile[] = []
  public fileInputModel: File[] = [] 
  public chunkSize: number | null = 1024
  public chunkOverlap: number | null = 64
  public directoryMode: boolean = false
  public embedProgress: number = 0
  public currentFileIndex: number = 0
  public currentFileName: string = ''
  public totalFilesToEmbed: number = 0
  public failedFiles: string[] = []
  public embeddingCancelled: boolean = false
  public successfulEmbeds: number = 0
  public sseSessionId: string | null = null
  public sseListener: ((event: any) => void) | null = null
  public processingStatusText: string = ''
  public isLoadingVectorStores!: boolean
  public isLoadingLocalCollections!: boolean
  public isLoadingCollections!: boolean
  public lastCollectionsRefresh!: number | null
  public manualRefresh = false
  public loadedCollections: Set<string> = new Set() // Track which collections have loaded files
  public loadingCollections: Set<string> = new Set() // Track which collections are currently loading
  public collectionVersions: Record<string, number> = {}
  
  private count_ = 0
  public showCreateModelOverlay = false
  public showEditModelOverlay = false
  public editingModelId: string | null = null
  public providerEmbeddingModels!: RosettaModel[]
  public modelConfigs!: Record<string, SystemModelConfig | UserModelConfig>
  public embeddingModels!: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
  public embeddingModelsById!: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
  public selectedTarget: UserEmbeddingModelConfig | null = null 

  // Search stuff for local collections
  public searchText: string = ''
  public searchTarget: UserEmbeddingModelConfig | null = null 
  public searchCollection: EmbeddingCollection | null = null 
  public searchTopK: number = 10; // Added for number of results
  public show: boolean = false
  public localCollections!: EmbeddingCollection[] 
  public searchResults!: EmbeddingSearchResult[] 
  public decodeBase64 = decodeBase64

  // File viewing dialog properties
  public showFileDialog: boolean = false
  public selectedFile: UserVectorFile | null = null
  public fileContent: string = ''
  public loadingFileContent: boolean = false
  public fileDialogTitle: string = ''

  get totalStorage(): string {
    if (!this.list || this.list.length === 0) {
      return this.formatBytes(0)
    }
    const totalBytes = this.list.reduce((acc, store) => acc + (store.bytes || 0), 0)
    return this.formatBytes(totalBytes)
  }

  get collectionsKey(): string {
    const totalBytes = this.list.reduce((acc, store) => acc + (store.bytes || 0), 0)
    const totalFiles = this.list.reduce((acc, store) => acc + (store.file_counts?.completed || 0), 0)
    const versions = Object.values(this.collectionVersions).join('-')
    return `collections-${this.list.length}-${totalFiles}-${totalBytes}-${versions}`
  }

  get count(): number {
    return this.count_
  }

  set count(value: number) {
    this.count_ = value
  }

  get embedDisabled(): boolean {
    return this.readingFiles || this.embeddingActive || this.selectedTarget == null || this.collection == null || this.fileInputModel.length === 0
  }

  get list(): VectorStoreWithFiles[] { 
    if (this.vectorStores != null) {
      return Object.values(this.vectorStores)
    }
    return []
  }
  
  get collectionNames(): { name: string, id: string, }[] { 
    return this.list.map(({ name, id }) => ({ name, id }))
  }

  get listTree(): TreeItem[] {
    return this.list.map((collection) => {
      const isLoading = this.loadingCollections.has(collection.id)
      const hasFiles = collection.files != null && collection.files.length > 0

      return {
        id: collection.id,
        vectorStoreId: collection.id,
        title: collection.name,
        safeName: sanitizeString(collection.name),
        subtitle: `${collection.file_counts.completed} files (${this.formatBytes(
          collection.bytes
        )}), created: ${new Date(collection.created_at).toLocaleString()}, model: ${
          collection.embeddingModel || 'unknown'
        }`,
        itemType: 'collection' as const,
        // Show loading indicator, files if present, or empty if not yet loaded
        children: isLoading
          ? [
              {
                id: `${collection.id}-loading`,
                title: 'Loading files...',
                itemType: 'file' as const,
                safeName: '',
                subtitle: ''
              }
            ]
          : hasFiles
          ? collection.files.map(({ id, filename, path, bytes }, i) => ({
              id: filename + i.toString() + id,
              fileId: id,
              title: `${
                path && path !== `uploads/${this.username}`
                  ? `${path.replace(`uploads/${this.username}/`, '')}/${filename}`
                  : filename
              } (${this.formatBytes(bytes)})`,
              safeName: '',
              subtitle: '',
              itemType: 'file' as const
            }))
          : [] // Empty if not loaded yet
      }
    })
  }

  async mounted() {
    this.trackComponentView('embeddings_page');
    await this.$store.dispatch('fetchProviders') 
    await this.$store.dispatch('fetchModelConfigs')
    this.updateEmbeddingModelIds()
    
    this.$store.dispatch('listVectorStores') 
    this.$store.dispatch('fetchLocalCollections') 
    this.setupSSE()
  }

  beforeUnmount() {
    this.cleanupSSE()
  }

  updateEmbeddingModelIds() {
    const embeddingModelIds = Object.keys(this.embeddingModelsById || {})
    this.count = embeddingModelIds.length
    console.log('Embedding models count:', this.count)
    if (this.count > 0) {
      const key = embeddingModelIds[0]
      console.log('Selected embedding model key:', key)
      if (key != null) {
        this.selectedTarget = this.embeddingModelsById[key] as UserEmbeddingModelConfig
        this.searchTarget = this.selectedTarget
      }
    }
  }

  handleModelDeleted() {
    this.updateEmbeddingModelIds()
  }

  removeModelCard() {
    if (this.count_ > this.savedEmbeddingModels.length) {
      this.count_--
    }
  }

  get isDesktop(): boolean {
    return (this as any).$vuetify.display.mdAndUp
  }

  openCreateModelOverlay() {
    this.trackButtonClick('create_embedding_model_start', 'click', {
      model_count: this.savedEmbeddingModels.length
    })
    this.showCreateModelOverlay = true
  }

  closeCreateModelOverlay() {
    this.showCreateModelOverlay = false
  }

  openEditModelOverlay(modelId: string) {
    this.editingModelId = modelId
    this.showEditModelOverlay = true
  }

  closeEditModelOverlay() {
    this.showEditModelOverlay = false
    this.editingModelId = null
  }

  handleEditedModelDeleted() {
    this.updateEmbeddingModelIds()
    this.closeEditModelOverlay()
  }

  handleModelSaved() {
    this.updateEmbeddingModelIds()
    this.showCreateModelOverlay = false
    this.showEditModelOverlay = false
    this.editingModelId = null
  }

  get savedEmbeddingModels(): (SystemEmbeddingModelConfig | UserEmbeddingModelConfig)[] {
    return Object.values(this.embeddingModelsById || {});
  }

  embeddingModelId(indexOrModel: number | RosettaModel): string {
    if (typeof indexOrModel === 'number') {
      return `embedding-model-${indexOrModel}-${Date.now()}`
    }
    return (indexOrModel != null && indexOrModel.id != null) ? `${indexOrModel.id}-${Date.now()}` : `${newId()}-${Date.now()}`
  }
  
  embeddingModelKey(index: number): string | undefined {
    if (index <= 0 || index > this.savedEmbeddingModels.length) {
      return undefined
    }
    const model = this.savedEmbeddingModels[index - 1];
    return model.config.id;
  }

  handleCollectionInput(value: string | null) {
    if (typeof value === 'string' && value) {
      const sanitized = modelNameToId(value)
      this.collection = {
        name: sanitized,
        id: sanitizeString(value)
      }
    }
  }

  addModel() {
    this.trackButtonClick('create_embedding_model_start', 'click');
    const savedModelsLength = this.savedEmbeddingModels.length
    if (this.count_ === savedModelsLength) {
      this.count_++
      console.log('Adding new embedding model card, count:', this.count_)
    } else {
      this.$store.dispatch('updateSnackbar', { 
        text: 'Please save the current embedding model before adding a new one',
        timeout: 3000
      })
    }
  }

  targetCollectionUpdated() {
    this.trackFormInteraction('embedding_form', 'update_field', 'collection_name');
    if (this.vectorStores != null && this.collection != null) {
      let vs: VectorStoreWithFiles | undefined;

      // When an existing item is selected, `collection` is an object with an id.
      if (typeof this.collection === 'object' && this.collection.id) {
        vs = this.vectorStores[this.collection.id];
      }
      // If a new name is typed, `collection` is a string, and we don't need to do anything here.

      if (vs) {
        // Update embedding model if available
        if (vs.embeddingModel) {
          const matchingModel = Object.values(this.embeddingModelsById).find(
            (model: SystemEmbeddingModelConfig | UserEmbeddingModelConfig) =>
              model.config.name.toLowerCase() === vs!.embeddingModel!.toLowerCase()
          );
          if (matchingModel) {
            this.selectedTarget = matchingModel as UserEmbeddingModelConfig;
          }
        }

        // Find local collection by name for chunk settings
        const localCollection = this.localCollections.find(
          (col: EmbeddingCollection) => col.name === vs!.name
        );

        if (localCollection) {
          this.chunkSize = localCollection.chunkSize;
          this.chunkOverlap = localCollection.chunkOverlap;
        }
      }
    }
  }

  searchCollectionUpdated() {
    if (this.searchCollection) {
      const modelName = this.searchCollection.model;
      if (modelName) {
        const matchingModel = Object.values(this.embeddingModelsById).find(
          (model: SystemEmbeddingModelConfig | UserEmbeddingModelConfig) =>
            model.config.name.toLowerCase() === modelName.toLowerCase()
        );
        if (matchingModel) {
          this.searchTarget = matchingModel as UserEmbeddingModelConfig;
        }
      }
    }
  }

  async search() {
    this.trackButtonClick('search_embeddings', 'click', {
      collection_name: this.searchCollection?.name,
      query_length: this.searchText.length,
      top_k: this.searchTopK
    });
    if (!this.searchCollection || !this.searchCollection.safeName) {
      this.$store.dispatch('updateSnackbar', { text: 'Choose a local collection to search.', timeout: 3_000 });
      return;
    }
    
    let embeddingModelName: string | undefined;
    if (this.searchTarget && this.searchTarget.config && typeof this.searchTarget.config.name === 'string') {
      embeddingModelName = this.searchTarget.config.name;
    }

    if (!embeddingModelName) {
      this.$store.dispatch('updateSnackbar', { text: 'Choose an embedding model for the search.', timeout: 3_000 });
      return;
    }
    if (!this.searchText.trim()) {
      this.$store.dispatch('updateSnackbar', { text: 'Enter search text.', timeout: 3_000 });
      return;
    }

    const collectionName = this.searchCollection.safeName;

    console.log(`Searching local collection: ${collectionName} with model: ${embeddingModelName} for query: "${this.searchText}", topK: ${this.searchTopK}`);

    await this.$store.dispatch('searchLocalCollectionAction', {
      collectionName,
      query: this.searchText,
      embeddingModelName,
      topK: this.searchTopK
    });
  }

  async deleteCollection(e: Event, item: TreeItem) {
    this.trackButtonClick('delete_embedding_collection', 'click', {
      collection_name: item.title,
      vector_store_id: item.vectorStoreId
    });
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
    
    if (item && item.itemType === 'collection' && item.vectorStoreId) {
      await this.$store.dispatch('deleteVectorStore', item.vectorStoreId)
      await this.$store.dispatch('listVectorStores')
    }
  }

  async deleteFile(e: Event, item: TreeItem) {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
  }

  async handleFileClick(item: TreeItem) {
    if (item.itemType !== 'file' || !item.fileId) return

    this.trackButtonClick('view_file', 'click', {
      file_id: item.fileId,
      file_name: item.title
    })

    // Extract filename from title (remove size info)
    const titleParts = item.title.match(/^(.*)\s\([^)]+\)$/)
    const filename = titleParts ? titleParts[1] : item.title

    if (shouldDownloadFile(filename)) {
      this.downloadFile(item.fileId, filename)
    } else {
      await this.viewFile(item.fileId, filename)
    }
  }

  downloadFile(fileId: string, filename: string) {
    // Create a download link using the API endpoint
    const link = document.createElement('a')
    link.href = `/v1/files/${fileId}/content`
    link.download = filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    this.$store.dispatch('updateSnackbar', {
      text: `Downloading ${filename}...`,
      timeout: 3000
    })
  }

  async viewFile(fileId: string, filename: string) {
    this.selectedFile = { id: fileId, filename: filename, bytes: 0, created_at: 0, path: '' }
    this.fileDialogTitle = filename
    this.loadingFileContent = true
    this.showFileDialog = true
    this.fileContent = ''

    try {
      const content = await this.$store.dispatch('fetchUserFileContent', fileId)
      if (content) {
        this.fileContent = content
      } else {
        throw new Error('No content received')
      }
    } catch (error) {
      console.error('Error fetching file content:', error)
      this.$store.dispatch('updateSnackbar', {
        text: 'Error loading file content',
        timeout: 3000
      })
      this.closeFileDialog()
    } finally {
      this.loadingFileContent = false
    }
  }

  closeFileDialog() {
    this.showFileDialog = false
    this.selectedFile = null
    this.fileContent = ''
    this.fileDialogTitle = ''
  }

  async embedFiles() {
    this.embeddingActive = true
    this.processingStatusText = 'Starting...'
    this.totalFilesToEmbed = this.fileInputModel.length
    this.successfulEmbeds = 0
    this.failedFiles = []

    // 1. Generate Session ID and connect to SSE endpoint
    this.sseSessionId = `${this.username}-${Date.now()}`
    this.$store.dispatch('connectSSE', this.sseSessionId)

    // 2. Upload all files first to get their server-side IDs
    this.processingStatusText = `Uploading ${this.totalFilesToEmbed} files...`
    const uploadedFileIds: string[] = []
    for (const file of this.fileInputModel) {
      if (this.embeddingCancelled) {
        this.processingStatusText = 'Embedding cancelled by user.'
        this.embeddingActive = false
        return
      }
      try {
        // Use existing 'addUserFile' action
        const addFiles: FileUpload[] | null = await this.$store.dispatch('addUserFile', {
          file,
          purpose: 'assistants',
          collectionName: this.collection?.name,
          relativePath: (file as any).webkitRelativePath
        })
        if (addFiles && addFiles.length > 0) {
          uploadedFileIds.push(addFiles[0].id)
        } else {
          this.failedFiles.push(file.name)
        }
      } catch (error) {
        this.failedFiles.push(file.name)
      }
    }

    if (uploadedFileIds.length === 0) {
      this.processingStatusText = 'File upload failed. Aborting.'
      this.embeddingActive = false
      return
    }
    this.processingStatusText = 'Files uploaded. Initializing embedding process...'

    // 3. Create the Vector Store and trigger async processing on the backend
    const collectionNameStr = this.collection && this.collection.name != null ? this.collection.name.trim() : this.collection?.name
    const embeddingModelName = this.selectedTarget?.config.name
    const chunkStrategy = {
      type: 'static',
      static: {
        max_chunk_size_tokens: Math.floor(this.chunkSize ?? 512),
        chunk_overlap_tokens: Math.floor(this.chunkOverlap ?? 32)
      }
    }

    // Dispatch the updated 'createVectorStore' action
    await this.$store.dispatch('createVectorStore', {
      name: collectionNameStr,
      file_ids: uploadedFileIds,
      embeddingModelName,
      chunking_strategy: chunkStrategy,
      sseSessionId: this.sseSessionId,
      enhancePDF: false // Assuming this is a data property
    })

    // The UI now waits for SSE events for progress.
    this.embedProgress = 0
  }

  setupSSE() {
    this.sseListener = (event: any) => {
      if (event.sessionId !== this.sseSessionId) return; // Ignore events not for this session

      switch (event.type) {
        case 'embedding_start':
          this.processingStatusText = `Processing: ${event.data.fileName}`
          break
        case 'embedding_page':
          this.processingStatusText = `Processing page ${event.data.currentPage} of ${event.data.totalPages} in ${event.data.fileName}.`
          break
        case 'embedding_vlm':
          this.processingStatusText = `Analyzing image on page ${event.data.page} of ${event.data.fileName} with VLM...`
          break
        case 'embedding_end':
          this.successfulEmbeds++
          this.embedProgress = Math.floor((this.successfulEmbeds / this.totalFilesToEmbed) * 100)
          this.processingStatusText = `Finished: ${event.data.fileName}`
          break
        case 'embedding_error':
          this.failedFiles.push(event.data.fileName)
          this.processingStatusText = `Error on ${event.data.fileName}: ${event.data.error}`
          break
        case 'embedding_batch_complete':
          this.processingStatusText = 'All processing complete.'
          this.embeddingActive = false // Hide progress bar
          this.fileInputModel = [] // Clear the file input
          Promise.all([
            this.$store.dispatch('listVectorStores'),
            this.$store.dispatch('fetchLocalCollections')
          ]).then(() => {
            this.$forceUpdate() // Force component re-render
          })
          break
      }
    }
    addEventListener(this.sseListener)
  }

  cleanupSSE() {
    if (this.sseListener) {
      removeEventListener(this.sseListener)
      this.sseListener = null
    }
    this.$store.dispatch('disconnectSSE') // Assumes a utility to close the connection
  }

  async selectedFilesChanged(files: File[]) {
    this.readingFiles = true
    const embedFiles: EmbedFile[] = []
    for (const file of files) {
      const { name, type, size, lastModified } = file
      const content = await this.getFileData(file)
      if (content != null) {
        const ef = { name, type, size, lastModified, content }
        embedFiles.push(ef)
      }
    }
    this.files = embedFiles 
    this.readingFiles = false
  }

  async getFileData(file: File) {
    const fileType = this.getFileType(file.name, file.type)
    if (fileType === 'text' || fileType === 'doc') {
      const reader = new FileReader()
      const readerPromise: Promise<string | undefined> = new Promise((resolve, reject) => {
        reader.onload = () => {
          return resolve(reader.result as string)
        }
        reader.onerror = () => {
          return reject(reader.error)
        }
      })
      reader.readAsText(file)
      return readerPromise
    }
    return undefined
  }

  getFileType(name: string, type: string) {
    if (isTextFileType(name)) {
      return 'text'
    }
    if (isDocFileType(name)) {
      return 'doc'
    }
    return undefined
  }

  toggleSearch(shouldShow: boolean) {
    this.show = shouldShow;
    this.trackButtonClick('toggle_embedding_search', shouldShow ? 'expand' : 'collapse');
  }

  async cancelEmbedding() {
    this.trackButtonClick('cancel_embedding', 'click')
    this.embeddingCancelled = true
    this.processingStatusText = 'Cancellation requested...'

    if (this.sseSessionId) {
      try {
        await this.$store.dispatch('cancelVectorStoreEmbedding', {
          sessionId: this.sseSessionId
        })
        // The backend will stop sending events. We can stop the progress locally.
        this.embeddingActive = false
      } catch (error) {
        console.error('Failed to send cancellation request:', error)
        this.processingStatusText = 'Cancellation failed. Please wait.'
      }
    }
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  treeOpenedWrapper() {
    // this.openedItems = openIds
    console.log('treeOpenedWrapper', this.openedItems)
    // this.handleTreeOpened(this.openedItems)
  }

  handleTreeOpened(openIds: unknown[]) {
    this.openedItems = openIds as string[]
    console.log('handleTreeOpened', openIds)
    console.log('listTree', this.openedItems)
    // Find newly opened collections
    for (const item of this.listTree) {
      if (item.itemType === 'collection' && item.vectorStoreId) {
        const isOpen = openIds.includes(item.id as string)
        const hasLoaded = this.loadedCollections.has(item.vectorStoreId)
        const isLoading = this.loadingCollections.has(item.vectorStoreId)

        // Load files if collection is opened and hasn't been loaded yet
        if (isOpen && !hasLoaded && !isLoading) {
          this.loadingCollections.add(item.vectorStoreId)
          if (item.vectorStoreId == null) {
            continue
          }
          // Use an async IIFE to handle the promise without making the main handler async
          ;(async () => {
            try {
              await this.$store.dispatch('fetchVectorStoreFiles', item.vectorStoreId)
              this.loadedCollections.add(item.vectorStoreId!)
              // Force a re-render by updating a version tracker
              this.collectionVersions[item.vectorStoreId!] = (this.collectionVersions[item.vectorStoreId!] || 0) + 1
            } finally {
              this.loadingCollections.delete(item.vectorStoreId!)
            }
          })()
        }
      }
    }
  }

  reloadOpenedCollectionFiles() {
    // Re-fetch files for any currently-opened collections whose file data was lost
    // (e.g., after listVectorStores replaced the store data)
    for (const item of this.listTree) {
      if (item.itemType === 'collection' && item.vectorStoreId) {
        const isOpen = this.openedItems.includes(item.id as string)
        const isLoading = this.loadingCollections.has(item.vectorStoreId)
        if (!isOpen || isLoading) continue

        // Check if the collection should have files but doesn't have them loaded
        const store = this.vectorStores[item.vectorStoreId]
        const expectedFiles = store?.file_counts?.completed ?? 0
        const hasFiles = store?.files != null && store.files.length > 0
        if (expectedFiles > 0 && !hasFiles) {
          this.loadingCollections.add(item.vectorStoreId)
          const vectorStoreId = item.vectorStoreId
          ;(async () => {
            try {
              await this.$store.dispatch('fetchVectorStoreFiles', vectorStoreId)
              this.loadedCollections.add(vectorStoreId)
              this.collectionVersions[vectorStoreId] = (this.collectionVersions[vectorStoreId] || 0) + 1
            } finally {
              this.loadingCollections.delete(vectorStoreId)
            }
          })()
        }
      }
    }
  }

  async refreshCollections() {
    this.trackButtonClick('refresh_collections', 'click', {
      manual: true,
      timestamp: new Date().toISOString()
    });
    
    this.manualRefresh = true
    
    try {
      // Run both requests in parallel
      await Promise.all([
        this.$store.dispatch('listVectorStores'),
        this.$store.dispatch('fetchLocalCollections')
      ])
      
      this.$store.dispatch('updateSnackbar', { 
        text: 'Collections refreshed successfully', 
        timeout: 2000 
      })
    } catch (error) {
      this.$store.dispatch('updateSnackbar', { 
        text: 'Error refreshing collections', 
        timeout: 3000 
      })
    } finally {
      this.manualRefresh = false
    }
  }
}

function isTextFileType(name: string): boolean {
  const textFileTypes = [
    '.txt', '.text', '.md', '.markdown', '.json', '.csv', '.yaml', '.toml', '.yml',
    '.js', '.jsx', '.ts', '.tsx', '.css', '.scss', '.sass', '.less', '.styl', '.html',
    '.htm', '.xml', '.xhtml', '.php', '.py', '.rb', '.go', '.c', '.h', '.cpp', '.cs', '.java',
    '.m', '.swift', '.vb', '.pl', '.sh', '.bat', '.cmd', '.vue', '.conf', '.ini',
    '.log', '.htaccess', '.htpasswd', '.env', '.dockerignore', '.gitignore', '.npmignore',
    '.babelrc', '.eslintrc', '.stylelintrc', '.prettierrc', '.gitconfig', '.npmrc', '.yarnrc',
    '.editorconfig', '.tf', '.tfvars', '.hcl', '.sql', '.svg'
  ]
  const lowerCaseName = name.toLowerCase()
  for (const fileType of textFileTypes) {
    if (lowerCaseName.endsWith(fileType)) {
      return true
    }
  }
  return false
}

function isImageFile(name: string): boolean {
  const imageFileTypes = [
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.ico', '.tif', '.tiff', '.webp'
  ]
  for (const fileType of imageFileTypes) {
    if (name.endsWith(fileType)) {
      return true
    }
  }
  return false
}

function isDocFileType(name: string): boolean {
  const documentFileTypes = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'
  ]
  const lowerCaseName = name.toLowerCase()
  for (const fileType of documentFileTypes) {
    if (lowerCaseName.endsWith(fileType)) {
      return true
    }
  }
  return false
}

function shouldDownloadFile(filename: string): boolean {
  const downloadExtensions = ['.xlsx', '.xls', '.docx', '.pptx', '.pdf']
  const lowerCaseName = filename.toLowerCase()
  return downloadExtensions.some(ext => lowerCaseName.endsWith(ext))
}
</script>

<style scoped lang="scss">
.display-block {
  display: block;
}
.embedding-search-results {
}
.file-input-constrained-height :deep(.v-field) {
  max-height: 150px;
  overflow-y: auto;
}
.file-link {
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: rgb(var(--v-theme-primary));
  }
}

.add-card {
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 200px;
  text-align: center;
  cursor: pointer;

  p {
    margin-top: 80px;
  }
}

.add-card-small {
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  cursor: pointer;
}
</style>
